import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import { UNDO_MS } from '../lib/swipe'
import { ABOVE_TAB_BAR } from '../shell/parts'

// ============================================================================
// THE DAY SCREEN'S UNDO TOAST — board 20's toast (a 54px slab capsule, 14px from each side, Undo
// on the right in the accent), on the phone's existing undo contract: one action, gone after
// UNDO_MS (4s, mobile/lib/swipe.ts — the Reminders screen's beat), never a celebration.
//
// Above the tab bar normally; 34px from the bottom while a sheet is open (board 23 draws it over
// the mail sheet there), and above the sheet's scrim either way.
// ============================================================================

export interface ToastMessage {
  text: string
  undo?: () => void
}

export function useToast() {
  const [toast, setToast] = useState<ToastMessage | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])
  const show = useCallback((message: ToastMessage) => {
    if (timer.current) clearTimeout(timer.current)
    setToast(message)
    timer.current = setTimeout(() => setToast(null), UNDO_MS)
  }, [])
  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    setToast(null)
  }, [])
  return { toast, show, clear }
}

export function DayToast({
  toast,
  overSheet,
  onDone,
}: {
  toast: ToastMessage | null
  overSheet: boolean
  onDone: () => void
}) {
  // The last message stays rendered while it fades out, so the capsule does not blink empty.
  const [last, setLast] = useState<ToastMessage | null>(toast)
  useEffect(() => {
    if (toast) setLast(toast)
  }, [toast])
  const shown = toast ?? last
  if (!shown) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'm-toast fixed inset-x-[14px] z-[60] flex h-[54px] items-center gap-3 rounded-[27px] bg-m-slab pl-[18px] pr-[6px] font-m text-m-on-slab',
        toast ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-[16px] opacity-0',
      )}
      style={{ bottom: overSheet ? 'calc(34px + env(safe-area-inset-bottom, 0px))' : ABOVE_TAB_BAR }}
    >
      {/* Two lines, not one: at the owner's 130% size "Marked handled — hidden from Day" no longer
          fits one line beside Undo, and a cut-off sentence is not the message. */}
      <span className="line-clamp-2 min-w-0 flex-1 text-[14.5px] font-bold leading-[1.2]">{shown.text}</span>
      {shown.undo && (
        <button
          type="button"
          onClick={() => {
            shown.undo?.()
            onDone()
          }}
          className="m-tap h-[42px] flex-none rounded-[21px] bg-m-slab-wash px-4 text-[14px] font-extrabold text-m-accent"
        >
          Undo
        </button>
      )}
    </div>
  )
}
